import {
  ClickIndicator,
  DragRectangle,
  getParticipantColor,
  ModernMousePointer,
} from '@flik/shared';
import '@flik/shared/components/annotations/ClickIndicator.css';
import React from 'react';
import { useCursorSharing } from '../hooks/useCursorSharing';
import { CursorPosition } from '../types/cursorEvents';

interface ScreenShare {
  id: string;
  participantIdentity: string;
  trackSid: string;
  publication: any;
}

interface ScreenShareVideoProps {
  share: ScreenShare;
  className?: string;
  session?: any;
  provideTailwind: (className: string) => string;
  getInitials: (identity: string) => string | 'LOADING';
  participantInfoMap: Map<string, any>;
  // Cursor sharing props
  room?: any; // LiveKit room instance
  isCursorSharingEnabled?: boolean;
}

const ScreenShareVideo: React.FC<ScreenShareVideoProps> = ({
  share,
  className = '',
  session,
  provideTailwind,
  getInitials,
  room,
  isCursorSharingEnabled = false,
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const isRegisteredRef = React.useRef<boolean>(false);
  const registrationAttemptedRef = React.useRef<boolean>(false);
  const [isVideoReady, setIsVideoReady] = React.useState<boolean>(false);
  const [videoBounds, setVideoBounds] = React.useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Mouse position for custom cursor
  const [mousePosition, setMousePosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  // Drag state management
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [dragStart, setDragStart] = React.useState<{
    x: number;
    y: number;
    normalizedX: number;
    normalizedY: number;
  } | null>(null);
  const [dragEnd, setDragEnd] = React.useState<{
    x: number;
    y: number;
    normalizedX: number;
    normalizedY: number;
  } | null>(null);
  const [justFinishedDragging, setJustFinishedDragging] =
    React.useState<boolean>(false);
  const dragEndTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [hasMouseMoved, setHasMouseMoved] = React.useState<boolean>(false);

  // Click indicator state
  const [clickIndicators, setClickIndicators] = React.useState<
    Array<{
      id: string;
      x: number;
      y: number;
      color: string;
    }>
  >([]);

  // Helper function to set drag end flag with timeout
  const setDragEndFlag = React.useCallback(() => {
    // Clear any existing timeout
    if (dragEndTimeoutRef.current) {
      clearTimeout(dragEndTimeoutRef.current);
    }

    setJustFinishedDragging(true);
    console.log(
      '🔒 Setting drag end flag - clicks will be blocked temporarily',
    );

    // Reset the flag after a short delay to allow normal clicks again
    dragEndTimeoutRef.current = setTimeout(() => {
      setJustFinishedDragging(false);
      dragEndTimeoutRef.current = null;
      console.log('🔓 Drag end flag cleared - clicks are now allowed');
    }, 50); // Reduced to 50ms for faster recovery
  }, []);

  // Helper function to remove click indicator
  const removeClickIndicator = React.useCallback((id: string) => {
    setClickIndicators((prev) =>
      prev.filter((indicator) => indicator.id !== id),
    );
  }, []);

  // Helper function to add click indicator
  const addClickIndicator = React.useCallback(
    (x: number, y: number, color: string) => {
      const id = `click-${Date.now()}-${Math.random()}`;
      setClickIndicators((prev) => [...prev, { id, x, y, color }]);
    },
    [],
  );

  // Generate consistent colors for participants (same logic as in annotation applet)

  // Use the current user's color for their cursor, not the screen sharer's color
  const currentUserIdentity = room?.localParticipant?.identity || 'unknown';
  const currentUserColor = getParticipantColor(currentUserIdentity);
  // Use the screen sharer's color for their initials circle
  const screenSharerColor = getParticipantColor(share.participantIdentity);

  // Cursor sharing hook
  const {
    publishCursorMove,
    publishCursorClick,
    publishCursorDragStart,
    publishCursorDrag,
    publishCursorDragEnd,
    isConnected,
  } = useCursorSharing({
    room,
    isEnabled: isCursorSharingEnabled,
    shareId: share.id,
    videoBounds,
  });

  const videoRefCb = React.useCallback(
    (el: HTMLVideoElement | null) => {
      // Prevent any re-registration if we've already attempted registration
      if (registrationAttemptedRef.current && isRegisteredRef.current) {
        return;
      }

      // Only register if the element actually changed and hasn't been registered yet
      if (el && el !== videoRef.current && !isRegisteredRef.current) {
        videoRef.current = el;
        isRegisteredRef.current = true;
        registrationAttemptedRef.current = true;
        session?.nonCameraVideoRefs?.current?.set(share.id, el);
        // Register the video element with LiveKit for proper track attachment
        session?.registerVideoElement?.(
          share.participantIdentity,
          el,
          false,
          share.trackSid,
        );
      } else if (!el && videoRef.current && isRegisteredRef.current) {
        session?.nonCameraVideoRefs?.current?.delete(share.id);
        // Unregister the video element
        session?.registerVideoElement?.(
          share.participantIdentity,
          null,
          false,
          share.trackSid,
        );
        videoRef.current = null;
        isRegisteredRef.current = false;
        registrationAttemptedRef.current = false;
      }
      // If element is the same, don't re-register to prevent flashing
      else if (el && el === videoRef.current && isRegisteredRef.current) {
        // Element is already registered, just ensure it's still in the refs
        session?.nonCameraVideoRefs?.current?.set(share.id, el);
      }
    },
    [share.id, share.participantIdentity, share.trackSid],
  );

  // Prevent re-registration when component re-renders due to other participant changes
  React.useEffect(() => {
    const video = videoRef.current;
    if (video && isRegisteredRef.current) {
      // Ensure the video element stays registered even during re-renders
      session?.nonCameraVideoRefs?.current?.set(share.id, video);
    }
  }, [share.id, session]);

  // Handle video events to maintain smooth transitions
  const handleVideoEvents = React.useCallback(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedData = () => {
        setIsVideoReady(true);
        video.style.opacity = '1';
      };

      const handleLoadStart = () => {
        // Keep the last frame visible during loading
        video.style.opacity = '0.95';
      };

      const handleCanPlay = () => {
        setIsVideoReady(true);
        video.style.opacity = '1';
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, []);

  // Set up video event listeners when video element is ready
  React.useEffect(() => {
    if (videoRef.current) {
      return handleVideoEvents();
    }
  }, [handleVideoEvents]);

  // Calculate video bounds for cursor tracking
  const calculateVideoBounds = React.useCallback(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) {
      return;
    }

    // Check if video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    // Get the actual video element's bounding rect, not the container
    const videoRect = video.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate the video bounds relative to the container
    const bounds = {
      top: videoRect.top - containerRect.top,
      left: videoRect.left - containerRect.left,
      width: videoRect.width,
      height: videoRect.height,
    };

    setVideoBounds(bounds);
  }, []);

  // Update video bounds when video loads or container resizes
  React.useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const updateBounds = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        calculateVideoBounds();
      }
    };

    // Update bounds when video metadata loads
    video.addEventListener('loadedmetadata', updateBounds);
    video.addEventListener('resize', updateBounds);

    // Update bounds when video element size changes
    video.addEventListener('loadeddata', updateBounds);
    video.addEventListener('canplay', updateBounds);

    // Update bounds on window resize
    window.addEventListener('resize', updateBounds);

    // Update bounds when container size changes
    const resizeObserver = new ResizeObserver(updateBounds);
    resizeObserver.observe(container);
    resizeObserver.observe(video);

    return () => {
      video.removeEventListener('loadedmetadata', updateBounds);
      video.removeEventListener('resize', updateBounds);
      video.removeEventListener('loadeddata', updateBounds);
      video.removeEventListener('canplay', updateBounds);
      window.removeEventListener('resize', updateBounds);
      resizeObserver.disconnect();
    };
  }, [calculateVideoBounds]);

  // Convert mouse position to normalized coordinates [0,1]
  const convertToNormalizedCoordinates = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!videoBounds || !containerRef.current) {
        return null;
      }

      const containerRect = containerRef.current.getBoundingClientRect();

      // Calculate relative position within the container
      const relativeX = clientX - containerRect.left;
      const relativeY = clientY - containerRect.top;

      // Check if the mouse is within the video content area
      if (
        relativeX >= videoBounds.left &&
        relativeX <= videoBounds.left + videoBounds.width &&
        relativeY >= videoBounds.top &&
        relativeY <= videoBounds.top + videoBounds.height
      ) {
        // Convert to normalized coordinates [0,1]
        const normalizedX = (relativeX - videoBounds.left) / videoBounds.width;
        const normalizedY = (relativeY - videoBounds.top) / videoBounds.height;

        return {
          x: Math.max(0, Math.min(1, normalizedX)), // Clamp to [0,1]
          y: Math.max(0, Math.min(1, normalizedY)), // Clamp to [0,1]
        };
      }

      return null;
    },
    [videoBounds],
  );

  // Handle cursor movement tracking
  const handleMouseMove = React.useCallback(
    (event: MouseEvent) => {
      // Only update mouse position if cursor sharing is enabled and mouse is over video
      if (isCursorSharingEnabled) {
        const normalizedCoords = convertToNormalizedCoordinates(
          event.clientX,
          event.clientY,
        );

        // Only show custom cursor when mouse is over video area
        if (normalizedCoords) {
          setMousePosition({ x: event.clientX, y: event.clientY });
        } else {
          setMousePosition(null);
        }
      }

      // Only process if cursor sharing is enabled and we're connected
      if (!isCursorSharingEnabled || !isConnected) {
        return;
      }

      const normalizedCoords = convertToNormalizedCoordinates(
        event.clientX,
        event.clientY,
      );

      // Only publish if we have valid normalized coordinates (mouse is over video)
      if (normalizedCoords) {
        const cursorPosition: CursorPosition = {
          x: normalizedCoords.x,
          y: normalizedCoords.y,
          participantId: room?.localParticipant?.identity || 'unknown',
          timestamp: Date.now(),
        };

        publishCursorMove(cursorPosition);

        // Check if we should start dragging (mouse down + movement)
        if (dragStart && !isDragging && !hasMouseMoved) {
          setHasMouseMoved(true);
          setIsDragging(true);

          const cursorPosition: CursorPosition = {
            x: dragStart.normalizedX,
            y: dragStart.normalizedY,
            participantId: room?.localParticipant?.identity || 'unknown',
            timestamp: Date.now(),
          };

          publishCursorDragStart(cursorPosition);
        }

        // Update drag end position if we're currently dragging
        if (isDragging && dragStart) {
          const dragEndData = {
            x: event.clientX,
            y: event.clientY,
            normalizedX: normalizedCoords.x,
            normalizedY: normalizedCoords.y,
          };
          setDragEnd(dragEndData);

          // Publish drag update
          const startPosition: CursorPosition = {
            x: dragStart.normalizedX,
            y: dragStart.normalizedY,
            participantId: room?.localParticipant?.identity || 'unknown',
            timestamp: Date.now(),
          };

          publishCursorDrag(startPosition, cursorPosition);
        }
      }
    },
    [
      convertToNormalizedCoordinates,
      isCursorSharingEnabled,
      isConnected,
      room?.localParticipant?.identity,
      publishCursorMove,
      isDragging,
      dragStart,
      publishCursorDrag,
      hasMouseMoved,
    ],
  );

  // Handle click events
  const handleClick = React.useCallback(
    (event: MouseEvent) => {
      console.log('🖱️ Click event received', {
        isCursorSharingEnabled,
        isConnected,
        justFinishedDragging,
      });

      // Only process if cursor sharing is enabled and we're connected
      if (!isCursorSharingEnabled || !isConnected) {
        console.log(
          '❌ Click blocked - cursor sharing disabled or not connected',
        );
        return;
      }

      // Don't send click event if we just finished dragging
      if (justFinishedDragging) {
        console.log('❌ Click blocked - just finished dragging');
        return;
      }

      const normalizedCoords = convertToNormalizedCoordinates(
        event.clientX,
        event.clientY,
      );

      // Only publish if we have valid normalized coordinates (mouse is over video)
      if (normalizedCoords) {
        const cursorPosition: CursorPosition = {
          x: normalizedCoords.x,
          y: normalizedCoords.y,
          participantId: room?.localParticipant?.identity || 'unknown',
          timestamp: Date.now(),
        };

        console.log('✅ Publishing click event', cursorPosition);
        publishCursorClick(cursorPosition);

        // Add click indicator
        addClickIndicator(
          normalizedCoords.x,
          normalizedCoords.y,
          currentUserColor,
        );
      } else {
        console.log('❌ Click blocked - not over video area');
      }
    },
    [
      convertToNormalizedCoordinates,
      isCursorSharingEnabled,
      isConnected,
      room?.localParticipant?.identity,
      publishCursorClick,
      justFinishedDragging,
      addClickIndicator,
      currentUserColor,
    ],
  );

  // Handle mouse down events for drag start
  const handleMouseDown = React.useCallback(
    (event: MouseEvent) => {
      // Only process if cursor sharing is enabled and we're connected
      if (!isCursorSharingEnabled || !isConnected) {
        return;
      }

      const normalizedCoords = convertToNormalizedCoordinates(
        event.clientX,
        event.clientY,
      );

      // Only start drag if we have valid normalized coordinates (mouse is over video)
      if (normalizedCoords) {
        const dragStartData = {
          x: event.clientX,
          y: event.clientY,
          normalizedX: normalizedCoords.x,
          normalizedY: normalizedCoords.y,
        };

        setDragStart(dragStartData);
        setHasMouseMoved(false); // Reset mouse movement flag
        // Don't set isDragging to true yet - wait for actual movement
      }
    },
    [convertToNormalizedCoordinates, isCursorSharingEnabled, isConnected],
  );

  // Handle mouse leave events to hide custom cursor
  const handleMouseLeave = React.useCallback(() => {
    // Hide custom cursor when mouse leaves the video area
    setMousePosition(null);
  }, []);

  // Handle mouse up events for drag end
  const handleMouseUp = React.useCallback(
    (event: MouseEvent) => {
      // If we have dragStart but weren't dragging, it was just a click
      if (dragStart && !isDragging) {
        setDragStart(null);
        setHasMouseMoved(false);
        return;
      }

      if (!isDragging || !dragStart) {
        return;
      }

      // Only process if cursor sharing is enabled and we're connected
      if (!isCursorSharingEnabled || !isConnected) {
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        return;
      }

      const normalizedCoords = convertToNormalizedCoordinates(
        event.clientX,
        event.clientY,
      );

      if (normalizedCoords) {
        const dragEndData = {
          x: event.clientX,
          y: event.clientY,
          normalizedX: normalizedCoords.x,
          normalizedY: normalizedCoords.y,
        };

        setDragEnd(dragEndData);

        const startPosition: CursorPosition = {
          x: dragStart.normalizedX,
          y: dragStart.normalizedY,
          participantId: room?.localParticipant?.identity || 'unknown',
          timestamp: Date.now(),
        };

        const endPosition: CursorPosition = {
          x: normalizedCoords.x,
          y: normalizedCoords.y,
          participantId: room?.localParticipant?.identity || 'unknown',
          timestamp: Date.now(),
        };

        publishCursorDragEnd(startPosition, endPosition);

        // Clear drag state immediately
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setDragEndFlag();
      } else {
        // Clear drag state immediately if not over video
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setDragEndFlag();
      }
    },
    [
      isDragging,
      dragStart,
      convertToNormalizedCoordinates,
      isCursorSharingEnabled,
      isConnected,
      room?.localParticipant?.identity,
      publishCursorDragEnd,
    ],
  );

  // Add event listeners for cursor tracking
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousemove', handleMouseMove, { passive: true });
    container.addEventListener('click', handleClick, { passive: true });
    container.addEventListener('mousedown', handleMouseDown, { passive: true });
    container.addEventListener('mouseup', handleMouseUp, { passive: true });
    container.addEventListener('mouseleave', handleMouseLeave, {
      passive: true,
    });

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('click', handleClick);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [
    handleMouseMove,
    handleClick,
    handleMouseDown,
    handleMouseUp,
    handleMouseLeave,
  ]);

  // Handle global mouse up to end drag if user drags outside container
  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setHasMouseMoved(false);
        setDragEndFlag();
      } else if (dragStart) {
        // Just a click, clean up
        setDragStart(null);
        setHasMouseMoved(false);
      }
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging]);

  // Cleanup drag state on unmount
  React.useEffect(() => {
    return () => {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      setHasMouseMoved(false);
      if (dragEndTimeoutRef.current) {
        clearTimeout(dragEndTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`relative ${className} flex items-center justify-center`}>
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center"
      >
        <div
          style={{
            cursor: 'none',
          }}
        >
          <video
            key={`screen-share-${share.id}-${share.trackSid}`}
            ref={videoRefCb}
            autoPlay
            playsInline
            className={`max-w-full max-h-full object-contain rounded-2xl shadow-lg border-2 ${provideTailwind(
              'border-primary',
            )} border-opacity-20`}
            style={{
              // Preserve the last frame during transitions
              objectFit: 'contain',
              backgroundColor: 'transparent',
              // Smooth opacity transitions to maintain last frame
              opacity: isVideoReady ? 1 : 0.95,
              transition: 'opacity 0.2s ease-in-out',
              // Ensure video content is preserved during re-registration
              willChange: 'opacity',
              // Prevent video element from being recreated
              pointerEvents: 'none',
            }}
            onLoadStart={(e) => {
              // Prevent any loading states that might cause flashing
              const video = e.currentTarget;
              if (video && isRegisteredRef.current) {
                video.style.opacity = '1';
              }
            }}
            onLoadedData={(e) => {
              // Ensure video stays visible
              const video = e.currentTarget;
              if (video && isRegisteredRef.current) {
                video.style.opacity = '1';
                setIsVideoReady(true);
              }
            }}
          />

          {/* Screen share indicator - positioned directly above the video content */}
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              // Position the overlay at the top of the actual video content
              top: videoBounds ? `${videoBounds.top + 16}px` : '1rem',
              left: videoBounds ? `${videoBounds.left + 16}px` : '1rem',
            }}
          >
            {/* Participant initials circle - always show, with loading state */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-white shadow-md"
              style={{
                backgroundColor: screenSharerColor,
                pointerEvents: 'none',
                userSelect: 'none',
                outline: 'none',
              }}
              tabIndex={-1}
            >
              {(() => {
                const initials = getInitials(share.participantIdentity);
                if (initials === 'LOADING') {
                  return (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  );
                }
                return (
                  <span className="text-white font-semibold text-base">
                    {initials}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Custom cursor overlay - only show when mouse is over video */}
      {mousePosition && videoBounds && isCursorSharingEnabled && (
        <div
          style={{
            position: 'fixed',
            left: mousePosition.x - 3, // Offset to position tip at cursor
            top: mousePosition.y - 3,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <ModernMousePointer color={currentUserColor} size={24} />
        </div>
      )}

      {/* Drag rectangle overlay */}
      {isDragging && dragStart && dragEnd && videoBounds && (
        <DragRectangle
          startX={videoBounds.left + dragStart.normalizedX * videoBounds.width}
          startY={videoBounds.top + dragStart.normalizedY * videoBounds.height}
          endX={videoBounds.left + dragEnd.normalizedX * videoBounds.width}
          endY={videoBounds.top + dragEnd.normalizedY * videoBounds.height}
          color={currentUserColor}
          isVisible={true}
        />
      )}

      {/* Click indicators overlay */}
      {clickIndicators.map((indicator) => (
        <div
          key={indicator.id}
          style={{
            position: 'absolute',
            left: videoBounds
              ? `${videoBounds.left + indicator.x * videoBounds.width}px`
              : '0px',
            top: videoBounds
              ? `${videoBounds.top + indicator.y * videoBounds.height}px`
              : '0px',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 1002,
          }}
        >
          <ClickIndicator
            x={0.5} // Center the indicator at the click position
            y={0.5}
            color={indicator.color}
            onAnimationComplete={() => removeClickIndicator(indicator.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default ScreenShareVideo;
